/**
 * Reiwa-aware date formatting. JP customers expect 元号 (era) dates
 * everywhere we surface a date — board reports, deal close dates, audit
 * trails. The Intl spec technically covers the ja-JP-u-ca-japanese
 * calendar, but the era abbreviations browsers ship (R8, H31, …) are
 * not what a Japanese reader wants to see on a CRM. Format it by hand.
 *
 * Eras supported here:
 *   令和 Reiwa  — from 2019-05-01    (year offset 2018)
 *   平成 Heisei — 1989-01-08 → 2019-04-30 (year offset 1988)
 *   昭和 Showa  — 1926-12-25 → 1989-01-07 (year offset 1925)
 *   大正 Taisho — 1912-07-30 → 1926-12-24 (year offset 1911)
 *
 * Years are not zero-padded (元号 numbering starts at 1). Months and
 * days are integers and left unpadded too — the canonical JP B2B
 * rendering. The optional `withTime` flag appends HH:MM (24h).
 */

type EraSpec = {
  name: string;
  start: Date; // inclusive
  offset: number; // gregorian year = era year + offset
};

const ERAS: readonly EraSpec[] = [
  { name: '令和', start: new Date('2019-05-01T00:00:00'), offset: 2018 },
  { name: '平成', start: new Date('1989-01-08T00:00:00'), offset: 1988 },
  { name: '昭和', start: new Date('1926-12-25T00:00:00'), offset: 1925 },
  { name: '大正', start: new Date('1912-07-30T00:00:00'), offset: 1911 },
];

function toDate(d: Date | string): Date {
  return d instanceof Date ? d : new Date(d);
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

export function formatReiwaDate(
  d: Date | string,
  opts: { withTime?: boolean } = {},
): string {
  const date = toDate(d);
  if (Number.isNaN(date.getTime())) return '';

  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  // Pick the most-recent era whose start is <= this date. Falls back to
  // the Gregorian year if the date predates all listed eras (rare, but
  // possible for very old test fixtures).
  let label = `${year}年${month}月${day}日`;
  for (const era of ERAS) {
    if (date.getTime() >= era.start.getTime()) {
      const eraYear = year - era.offset;
      label = `${era.name}${eraYear}年${month}月${day}日`;
      break;
    }
  }

  if (opts.withTime) {
    const hh = pad2(date.getHours());
    const mm = pad2(date.getMinutes());
    return `${label} ${hh}:${mm}`;
  }
  return label;
}

/**
 * Locale-aware date formatter. When the active language is Japanese,
 * delegate to {@link formatReiwaDate} so era dates render natively.
 * For every other locale, fall back to Intl.DateTimeFormat with a
 * medium date style (and short time when requested).
 */
export function formatLocaleDate(
  d: Date | string,
  locale: string,
  opts: { withTime?: boolean } = {},
): string {
  const date = toDate(d);
  if (Number.isNaN(date.getTime())) return '';

  if (locale && locale.toLowerCase().startsWith('ja')) {
    return formatReiwaDate(date, opts);
  }

  try {
    return new Intl.DateTimeFormat(locale || undefined, {
      dateStyle: 'medium',
      timeStyle: opts.withTime ? 'short' : undefined,
    }).format(date);
  } catch {
    // Browsers reject some BCP-47 tags; fall through to the platform
    // default rather than crashing the page.
    return opts.withTime ? date.toLocaleString() : date.toLocaleDateString();
  }
}
